import { useState } from 'react';
import { ALL_TILES, parseFactId } from '../engine/facts';
import type { FactId, Tile } from '../engine/facts';
import type { MasteryState } from '../engine/mastery';
import { expr } from './format';
import { Hint, Screen, Title } from './kit';
import { MASTERY_COLOR, MASTERY_LABEL, MASTERY_ORDER, MASTERY_SHORT } from './mastery-view';
import { ringArcs, sumCounts, tileBreakdown, tileFactIds } from './progress';
import type { Counts } from './progress';

const RADIUS = 26;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function Ring({ counts }: { counts: Counts }) {
  return (
    <svg viewBox="0 0 64 64" className="size-16 flex-none -rotate-90" aria-hidden="true">
      {ringArcs(counts, CIRCUMFERENCE).map((arc) => (
        <circle
          key={arc.state}
          cx="32"
          cy="32"
          r={RADIUS}
          fill="none"
          stroke={MASTERY_COLOR[arc.state]}
          strokeWidth="9"
          strokeDasharray={`${arc.length} ${CIRCUMFERENCE - arc.length}`}
          strokeDashoffset={-arc.offset}
        />
      ))}
    </svg>
  );
}

/** Число рядом с цветом, а не вместо него: §10 требует не полагаться на
 *  один канал, и родителю всё равно нужны количества, а не только доли. */
function CountsLine({ counts }: { counts: Counts }) {
  const shown = MASTERY_ORDER.filter((state) => counts[state] > 0);
  return (
    <p className="m-0 flex flex-wrap gap-x-3 gap-y-1 text-[0.85rem] text-muted">
      {shown.map((state) => (
        <span key={state} className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="inline-block size-2.5 rounded-full"
            style={{ background: MASTERY_COLOR[state] }}
          />
          {MASTERY_LABEL[state]}: {counts[state]}
        </span>
      ))}
    </p>
  );
}

interface Props {
  states: ReadonlyMap<FactId, MasteryState>;
  onFact: (factId: FactId) => void;
  onBack: () => void;
}

export function ProgressScreen({ states, onFact, onBack }: Props) {
  const [open, setOpen] = useState<Tile | null>(null);

  const perTile = ALL_TILES.map((tile) => ({ tile, counts: tileBreakdown(tile, states) }));
  const total = sumCounts(perTile.map((row) => row.counts));
  const all = MASTERY_ORDER.reduce((sum, state) => sum + total[state], 0);

  return (
    <Screen>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          aria-label="Назад"
          className="size-11 flex-none rounded-full bg-card text-[1.1rem] text-muted"
        >
          ←
        </button>
        <Title>Успехи</Title>
      </div>

      <p className="m-0 text-[1.35rem] font-semibold">
        Освоено {total.mastered} из {all}
      </p>
      <CountsLine counts={total} />

      <ul className="m-0 grid list-none gap-2 p-0">
        {perTile.map(({ tile, counts }) => (
          <li key={tile} className="rounded-xl border border-line bg-card">
            <button
              type="button"
              aria-expanded={open === tile}
              onClick={() => setOpen((current) => (current === tile ? null : tile))}
              className="flex w-full items-center gap-3 p-3 text-left"
            >
              <Ring counts={counts} />
              {/* Раздел 6.3: рядом с КАЖДЫМ кольцом состояния перечислены
                  словом и количеством (§10). Одной строки «освоено N из 18»
                  мало — остальные семнадцать различались только цветом дуг,
                  а именно вопрос «чего сколько учить» этот экран и решает. */}
              <span className="grid gap-1">
                <span className="text-[1.05rem] font-semibold">Таблица на {tile}</span>
                <CountsLine counts={counts} />
              </span>
              <span aria-hidden="true" className="ml-auto text-muted">
                {open === tile ? '▴' : '▾'}
              </span>
            </button>

            {/* Две колонки, а не три: рядом с примером должно помещаться
                слово. Состояние, показанное одним лишь цветом, для ребёнка с
                дальтонизмом и родителя на дешёвом экране не существует (§10),
                а именно этот экран — единственное место, где виден срез по
                каждому примеру. */}
            {open === tile && (
              <ul className="m-0 grid list-none grid-cols-2 gap-2 border-t border-line p-3 pt-3">
                {tileFactIds(tile).map((factId) => {
                  const state = states.get(factId) ?? 'never';
                  return (
                    <li key={factId}>
                      <button
                        type="button"
                        onClick={() => onFact(factId)}
                        className="flex min-h-11 w-full items-center gap-2 rounded-lg border border-line px-2 py-1.5 text-sm tabular-nums"
                      >
                        <span>{expr(parseFactId(factId))}</span>
                        <span
                          aria-hidden="true"
                          className="ml-auto size-2.5 flex-none rounded-full"
                          style={{ background: MASTERY_COLOR[state] }}
                        />
                        <span className="text-[0.7rem] text-muted">{MASTERY_SHORT[state]}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        ))}
      </ul>

      {all > 0 && total.never === all && (
        <Hint>Пока ничего не встречалось — статистика появится после первой тренировки</Hint>
      )}
    </Screen>
  );
}
